/**
 * Minor Electrical Installation Works Certificate (MEIWC) - PDF Transform
 * Task 1.3.2: Includes certificate verification QR payload
 */
import { ZsCalculator } from '../../utils/electrical/bs7671Tables.js';

const parseDevice = (type, rating, disconnectionTime = '0.4s', isRCD = false) => {
  const typeParts = type.split(' ');
  const base = typeParts[0]; // MCB, RCBO, BS
  const variant = typeParts.slice(1).join(' '); // 'Type B', '88-2', etc.

  return {
    type: base === 'MCB' ? 'MCB' : base === 'RCBO' ? 'RCBO' : 'Fuse',
    typeVariant: base === 'BS' ? `BS ${variant}` : variant,
    rating: parseInt(rating, 10) || 0,
    disconnectionTime,
    is30mARCD: isRCD
  };
};

/**
 * Build the certificate verification QR payload (serialized as compact JSON).
 * This is embedded in the PDF QR code for authenticity verification.
 */
export const buildVerificationPayload = (formData) => {
  const circuit = formData.circuitDetails?.[0] || {};
  return {
    type: 'MEIWC',
    certNo: formData.worksDescription?.certificateNumber || '',
    date: formData.worksDescription?.dateOfWorks || '',
    client: formData.worksDescription?.clientName || '',
    address: formData.worksDescription?.installationAddress || '',
    engineer: formData.declaration?.engineerName || '',
    card: formData.declaration?.engineerCardNumber || '',
    device: circuit.protectiveDevice || '',
    rating: circuit.deviceRating || '',
    measuredZs: circuit.measuredZs || '',
    result: circuit.testResult || ''
  };
};

export const transformForPDF = (formData) => {
  const circuits = (formData.circuitDetails || []).map(circuit => {
    let maxZs = circuit.maxZs;
    let zsStatus = 'pending';
    let zsDeviation = null;

    // Auto-compute max Zs from device
    if (circuit.protectiveDevice && circuit.deviceRating && !maxZs) {
      maxZs = ZsCalculator.getMaxZs(parseDevice(circuit.protectiveDevice, circuit.deviceRating));
    }

    // Determine Zs compliance
    if (maxZs && circuit.measuredZs !== '' && circuit.measuredZs !== null && circuit.measuredZs !== undefined) {
      const measured = parseFloat(circuit.measuredZs);
      const compliance = ZsCalculator.checkZsCompliance(measured, parseFloat(maxZs));
      zsStatus = compliance.status;
      zsDeviation = measured - parseFloat(maxZs);
    }

    // Insulation resistance compliance
    let irStatus = 'pending';
    if (circuit.insulationResistance !== '' && circuit.insulationResistance !== null && circuit.insulationResistance !== undefined) {
      irStatus = parseFloat(circuit.insulationResistance) >= 1.0 ? 'PASS' : 'FAIL';
    }

    // RCD trip time compliance (1Δn <= 300 ms at 100% rated residual current)
    let rcdStatus = circuit.rcdType && circuit.rcdType !== 'None' ? 'pending' : 'N/A';
    if (circuit.rcdType && circuit.rcdType !== 'None' && circuit.rcdTripTime1x !== '' && circuit.rcdTripTime1x !== null && circuit.rcdTripTime1x !== undefined) {
      rcdStatus = parseFloat(circuit.rcdTripTime1x) <= 300 ? 'PASS' : 'FAIL';
    }

    return {
      ...circuit,
      maxZs: maxZs || '',
      zsStatus,
      zsDeviation: zsDeviation !== null ? zsDeviation.toFixed(2) : '',
      irStatus,
      rcdStatus
    };
  });

  // Overall result: any UNSATISFACTORY result forces certificate to reflect it
  const hasUnsatisfactory = circuits.some(c => c.testResult === 'UNSATISFACTORY');
  const overallAssessment = hasUnsatisfactory ? 'UNSATISFACTORY' : 'SATISFACTORY';

  return {
    title: 'MINOR ELECTRICAL INSTALLATION WORKS CERTIFICATE',
    subtitle: 'BS 7671 Model Form for Minor Works (IET / NICEIC / NAPIT)',
    standard: 'BS 7671:2018+A2:2022 (IET Wiring Regulations 18th Edition)',
    template: 'meiwc',
    verification: buildVerificationPayload(formData),
    worksDescription: {
      certificateNumber: formData.worksDescription?.certificateNumber || '',
      dateOfWorks: formData.worksDescription?.dateOfWorks || '',
      clientName: formData.worksDescription?.clientName || '',
      clientAddress: formData.worksDescription?.clientAddress || '',
      installationAddress: formData.worksDescription?.installationAddress || '',
      descriptionOfWorks: formData.worksDescription?.descriptionOfWorks || '',
      existingInstallation: formData.worksDescription?.existingInstallation || ''
    },
    earthingBonding: {
      earthingArrangement: formData.earthingBonding?.earthingArrangement || '',
      earthConductor: formData.earthingBonding?.earthConductor || '',
      mainBonding: formData.earthingBonding?.mainBonding || '',
      supplementaryBonding: formData.earthingBonding?.supplementaryBonding || '',
      bondingNotes: formData.earthingBonding?.bondingNotes || ''
    },
    circuits,
    overallAssessment,
    declaration: {
      engineerName: formData.declaration?.engineerName || '',
      engineerCardNumber: formData.declaration?.engineerCardNumber || '',
      companyName: formData.declaration?.companyName || '',
      schemeName: formData.declaration?.schemeName || '',
      schemeReference: formData.declaration?.schemeReference || '',
      engineerSignature: formData.declaration?.engineerSignature || '',
      customerSignature: formData.declaration?.customerSignature || ''
    }
  };
};

export default transformForPDF;